/**
 * Live job costing, on the device.
 *
 * A port of `app/services/costing.py`, and deliberately a *port* rather than
 * an approximation: the tech's phone and the office's server have to agree
 * about a number the customer will be billed for. Both are held to the same
 * cases in `tests/fixtures/costing_cases.json`, so a change to one that the
 * other does not follow fails a build rather than becoming an argument about
 * an invoice six weeks later.
 *
 * Duplicating the rules in two languages is a real cost. The alternative —
 * a phone that cannot tell a tech their margin without a signal — defeats the
 * thing the product is for, and the rules here are forty lines that change
 * rarely. If that stops being true, the honest answer is a shared WASM build,
 * not letting the two drift.
 */

/** Why a job is refusing further time or parts. Mirrors `costing.Guard`. */
export type Guard = "none" | "over_quoted_hours" | "below_break_even" | "unapproved_variation";

export type CostLine = {
  description: string;
  qty: number;
  unit_cost_cents: number;
  unit_sell_cents: number;
  kind?: string;
  billable?: boolean;
  under_warranty?: boolean;
};

export type CostTime = {
  minutes: number;
  cost_cents_per_hour: number;
  charge_cents_per_hour: number;
  billable?: boolean;
  kind?: string;
};

export type JobCosting = {
  revenue_cents: number;
  cost_cents: number;
  margin_cents: number;
  margin_bp: number;
  labour_cost_cents: number;
  material_cost_cents: number;
  hours_actual: number;
  hours_quoted: number | null;
  quoted_cents: number | null;
  headroom_cents: number | null;
  guard: Guard;
  warnings: string[];
};

/**
 * Round a money amount to whole cents, half away from zero.
 *
 * `Math.round` is half *up* — it rounds -0.5 to -0, not to -1 — which is the
 * wrong answer on a credit. The sign is handled explicitly so this matches
 * Python's `Decimal(..., ROUND_HALF_UP)` twin in `app/services/costing.py`,
 * including the negative cases a credit note produces.
 */
export function cents(value: number): number {
  // Nudged before rounding: floating-point means 3162.5 is exact but
  // 0.1 * 3 is not, and a cent lost to representation is still a cent lost.
  const scaled = Math.round(Math.abs(value) * 1e6) / 1e6;
  return Math.sign(value) * Math.round(scaled) || 0;
}

/** Margin in basis points. Mirrors `pricing.margin_bp`. */
export function marginBp(costCents: number, sellCents: number): number {
  if (sellCents === 0) return 0;
  return cents(((sellCents - costCents) / sellCents) * 10_000);
}

function lineCost(line: CostLine): number {
  return cents(line.unit_cost_cents * line.qty);
}

function lineRevenue(line: CostLine): number {
  if (line.billable === false || line.under_warranty === true) return 0;
  return cents(line.unit_sell_cents * line.qty);
}

function timeCost(entry: CostTime): number {
  return cents((entry.minutes / 60) * entry.cost_cents_per_hour);
}

function timeRevenue(entry: CostTime): number {
  if (entry.billable === false) return 0;
  return cents((entry.minutes / 60) * entry.charge_cents_per_hour);
}

export type CostOptions = {
  /** The fixed price the customer agreed. Absent means time and materials. */
  quoted_cents?: number | null;
  quoted_hours?: number | null;
  break_even_margin_bp?: number;
  approved_variation_cents?: number;
  has_unapproved_variation?: boolean;
  /**
   * A service call's fee, 15-minute blocks and km (D86), which bill in place
   * of the hours themselves. Mirrors `costing.cost_job(service_revenue_cents=)`.
   */
  service_revenue_cents?: number | null;
  /** A discount or buffer on everything the job bills (F9, D87). */
  adjustment_bp?: number | null;
  adjustment_cents?: number | null;
};

/**
 * Roll a job up to one set of numbers, with the reason it is blocked if it is.
 *
 * `quoted_cents` present means revenue is the quote plus *approved* variations
 * — **not** the sum of the lines. On a fixed-price job the lines are cost
 * tracking, not billing, and summing their sell prices reports a healthy
 * margin on a job that lost money (D7).
 */
function adjustmentAmount(baseCents: number, bp: number | null, fixedCents: number | null): number {
  // Inlined rather than imported from pricing.ts, which imports this module.
  let amount: number;
  if (fixedCents !== null && fixedCents !== undefined) amount = Math.trunc(fixedCents);
  else if (bp) amount = cents((baseCents * bp) / 10_000);
  else return 0;
  return Math.max(amount, -Math.max(baseCents, 0));
}

export function costJob(lines: CostLine[], times: CostTime[], options: CostOptions = {}): JobCosting {
  const {
    quoted_cents = null,
    quoted_hours = null,
    break_even_margin_bp = 3100,
    approved_variation_cents = 0,
    has_unapproved_variation = false,
    service_revenue_cents = null,
    adjustment_bp = null,
    adjustment_cents = null,
  } = options;

  const materialCost = lines.reduce((sum, line) => sum + lineCost(line), 0);
  const labourCost = times.reduce((sum, entry) => sum + timeCost(entry), 0);
  const cost = materialCost + labourCost;

  const base =
    quoted_cents !== null
      ? quoted_cents + approved_variation_cents
      : service_revenue_cents !== null
        ? lines.reduce((sum, line) => sum + lineRevenue(line), 0) + service_revenue_cents
        : lines.reduce((sum, line) => sum + lineRevenue(line), 0) +
          times.reduce((sum, entry) => sum + timeRevenue(entry), 0);

  // Mirrors `costing.cost_job`: the same amount the invoice carries as a line.
  const revenue = base + adjustmentAmount(base, adjustment_bp, adjustment_cents);

  const minutes = times.reduce((sum, entry) => sum + entry.minutes, 0);
  const hoursActual = minutes / 60;
  const bp = marginBp(cost, revenue);

  const warnings: string[] = [];
  let guard: Guard = "none";

  // The priority order is the point: the highest one is the only one shown,
  // because a screen with three warnings on it has none (D8).
  if (has_unapproved_variation) {
    guard = "unapproved_variation";
    warnings.push("A variation is waiting on approval before more time can be booked.");
  }

  if (quoted_hours !== null && hoursActual > quoted_hours) {
    warnings.push(`${(hoursActual - quoted_hours).toFixed(1)} hours over the ${quoted_hours.toFixed(1)} quoted.`);
    if (guard === "none") guard = "over_quoted_hours";
  }

  if (revenue > 0 && bp < break_even_margin_bp) {
    warnings.push(
      `Margin ${(bp / 100).toFixed(1)}% is below the ${Math.round(break_even_margin_bp / 100)}% break-even.`,
    );
    if (guard === "none") guard = "below_break_even";
  }

  return {
    revenue_cents: revenue,
    cost_cents: cost,
    margin_cents: revenue - cost,
    margin_bp: bp,
    labour_cost_cents: labourCost,
    material_cost_cents: materialCost,
    hours_actual: Math.round(hoursActual * 100) / 100,
    hours_quoted: quoted_hours,
    quoted_cents,
    headroom_cents: quoted_cents !== null ? quoted_cents + approved_variation_cents - cost : null,
    guard,
    warnings,
  };
}
